var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import './GraphLegend.scss';
import { ClassificationLegend } from './ClassificationLegend';
import { DeferredRender } from '@ui/general';
import { error } from '@sonar-software/xuder';
import { GraphZoomButtons } from './GraphZoomButtons';
import { GroupLegend } from './GroupLegend';
import poweredByArovy from 'images/poweredByArovy.png';
import { SystemOverview } from './SystemOverview';
import { When } from 'react-if';
import { Button, Checkbox, Modal, Row, Space } from 'antd';
import { COPY_SPECIFIC, DATA_SECURITY_DASHBOARD } from '@sonar-software/sonar-ts-constants';
import { DownloadOutlined, ExpandAltOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import { GraphinContext } from '@antv/graphin';
import { off, on, trigger } from '@lib/events';
import { OrganizeBySelect } from './OrganizeBySelect';
import React, { useCallback, useContext, useState } from 'react';
import { useAction, useAppNotifications, useOnMount } from '@hooks';
const closeOverviewEvent = 'system_overview_close';
function downloadFile(blob) {
    if (blob) {
        const blobUrl = URL.createObjectURL(blob);
        const tempLink = document.createElement('a');
        tempLink.style.display = 'none';
        tempLink.href = blobUrl;
        tempLink.setAttribute('download', 'sonar-system-overview-graph.png');
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
        setTimeout(() => {
            // For Firefox it is necessary to delay revoking the ObjectURL
            URL.revokeObjectURL(blobUrl);
        }, 100);
    }
}
export function addWatermark(fileUrl) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = fileUrl;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Failed to get canvas context'));
                return;
            }
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0, img.width, img.height);
            const watermark = new Image();
            watermark.src = poweredByArovy;
            watermark.onload = () => {
                const watermarkPadding = 20;
                const watermarkWidth = 125;
                const watermarkHeight = (watermark.height / watermark.width) * watermarkWidth;
                const watermarkX = canvas.width - watermarkWidth - (2 * watermarkPadding);
                const watermarkY = canvas.height - watermarkHeight - (2 * watermarkPadding);
                ctx.drawImage(watermark, watermarkX, watermarkY, watermarkWidth, watermarkHeight);
                canvas.toBlob((blob) => {
                    if (blob) {
                        resolve(blob);
                    }
                    else {
                        reject(new Error('Failed to create blob from canvas'));
                    }
                });
            };
        };
    });
}
export const GraphLegend = ({ extra, expanded, groupIds, hideDetected, onHideDetectedChange, onOrganizeByChange, organizeBy }) => {
    const { graph } = useContext(GraphinContext);
    const logError = useAction(error);
    const { errorNotification } = useAppNotifications();
    const [isExpanded, setIsExpanded] = useState(false);
    useOnMount(() => {
        const handler = () => {
            setIsExpanded(false);
        };
        on(closeOverviewEvent, handler);
        return () => off(closeOverviewEvent, handler);
    });
    const handleDownloadGraph = useCallback(() => {
        graph.toFullDataURL((res) => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const blob = yield addWatermark(res);
                if (blob) {
                    downloadFile(blob);
                }
                else {
                    throw new Error('Unable to download file.');
                }
            }
            catch (ex) {
                logError(ex);
                errorNotification('error');
            }
        }), 'image/png', {
            backgroundColor: '#fff',
            padding: 125
        });
    }, [errorNotification, graph, logError]);
    return (React.createElement("div", { className: 'app-overview-legend' },
        React.createElement(Space, { direction: 'vertical', style: { width: '100%' } },
            React.createElement(Row, { align: 'bottom', justify: 'space-between' },
                React.createElement("div", { className: 'controls' },
                    React.createElement("div", { className: 'controls-header' }, COPY_SPECIFIC.CONTROLS),
                    React.createElement(Checkbox, { checked: hideDetected, className: 'hide-detected-apps-checkbox', onChange: (e) => onHideDetectedChange(e.target.checked) }, DATA_SECURITY_DASHBOARD.HIDE_DETECTED_APPS),
                    React.createElement(OrganizeBySelect, { onChange: onOrganizeByChange, value: organizeBy }),
                    extra),
                React.createElement(Space, { size: 'small' },
                    React.createElement(GraphZoomButtons, null),
                    React.createElement(Button, { icon: React.createElement(DownloadOutlined, null), id: 'download-graph-button', onClick: handleDownloadGraph }),
                    React.createElement(Button, { icon: expanded ? React.createElement(FullscreenExitOutlined, null) : React.createElement(ExpandAltOutlined, null), id: 'expand-graph-button', onClick: () => {
                            if (expanded) {
                                trigger(closeOverviewEvent);
                            }
                            else {
                                setIsExpanded(!isExpanded);
                            }
                        } }))),
            React.createElement(When, { condition: organizeBy === 'group' },
                React.createElement(GroupLegend, { groupIds: groupIds })),
            React.createElement(When, { condition: organizeBy === 'classification' },
                React.createElement(ClassificationLegend, { groupIds: groupIds }))),
        React.createElement(Modal, { centered: true, className: 'system-overview-modal', destroyOnClose: true, footer: null, keyboard: true, maskClosable: true, onCancel: () => setIsExpanded(false), onClose: () => setIsExpanded(false), open: isExpanded },
            React.createElement(DeferredRender, { delay: 200 },
                React.createElement(SystemOverview, { expanded: true })))));
};
